import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ModalsWrapperContext } from '@kanda-libs/ks-component-ts';
import type { Company, UserType } from '@kanda-libs/ks-frontend-services';

import {
  DECLINE_MODAL_ID,
  VERIFY_MODAL_ID,
} from 'components/company/CompanyLayout/CompanyDetails/constants';
import {
  FormattedCompanyInfo,
  formatCompanyInfo,
} from 'components/company/CompanyLayout/CompanyDetails/helpers';
import useChangeDepositState from 'components/company/CompanyLayout/CompanyDetails/useChangeDepositState';
import companySelectors from 'store/selectors/company';

export interface CompanyDetailsHook extends FormattedCompanyInfo {
  isSolar: boolean;
  changeDepositState: () => void;
  isSubmitting: boolean;
  ownDeposit: boolean;
  company?: Company;
  showDeclineModal: () => void;
  showVerifyModal: () => void;
  billing: string;
  users?: UserType[];
}

export default function useCompanyDetails(): CompanyDetailsHook {
  const company = useSelector(companySelectors.getEntity);
  const isSubmitting = useSelector(companySelectors.getIsSubmitting);
  const { showModal } = useContext(ModalsWrapperContext);

  const isSolar = !!company?.solar_company_info;

  const info = formatCompanyInfo(company, isSolar);

  const ownDeposit = info?.skipDeposit === 'Yes';

  const [changeDepositState, changingDepositState] = useChangeDepositState();

  const showDeclineModal = () => showModal(DECLINE_MODAL_ID);
  const showVerifyModal = () => showModal(VERIFY_MODAL_ID);

  const users = useMemo(() => {
    if (!company) return undefined;
    const userArray = company.users;
    const name = company?.contact_info?.contact_name?.split(' ');
    const email = company?.contact_info?.contact_email;
    const phone = company?.contact_info?.contact_phone;
    if (!email) return userArray;
    const contactUser: UserType = {
      role: 'company-admin',
      first_name: name?.[0] || '--',
      last_name: name?.slice(1)?.join(' '),
      email,
      mobile: phone,
    };
    if (!userArray) return [contactUser];
    return [contactUser, ...userArray];
  }, [company]);

  return {
    ...info,
    isSolar,
    isSubmitting,
    ownDeposit,
    changeDepositState,
    company,
    showDeclineModal,
    showVerifyModal,
    billing: company?.billing || 'No billing',
    users,
  };
}
